// Daily and weekly quests, and the weekly chest.
//
// Daily quests: three a day, drawn from a pool seeded by the UTC date, so
// trainers of the same stage share them. Kinds, targets and rewards are sized
// to the trainer when the day's quests are first made: a new trainer gets no
// Bloodline or grade catches and smaller targets (see QUEST_STAGES). They
// progress from the hunt's own events, idle or not; claiming is a button and
// never needed to keep hunting. A day's finished quests can still be claimed
// the next day, then they lapse.
//
// Weekly quests work the same way over a UTC week (Monday to Sunday): three a
// week with about ten times the daily targets and five times the daily
// rewards. From mid levels the weekly pool adds a boss win and Arena battles.
//
// Rewards are supplies a trainer would otherwise buy, sized to the best hunt
// open when the set is made, so they stay worth having from Lv 1 to Lv 1,000+:
// coins (kills' worth at that hunt), seals for catch quests, healing potions
// for defeat quests, and a short XP boost for the boss and Arena quests.
//
// Hourly quests (from Lv HOURLY_FROM): one a UTC hour, a short errand on one
// of the trainer's lower hunts (defeat wilds there, or catch its species), so
// an active player always has something to do between the daily ones. Each is
// about ten minutes of hunting there. They pay coins and a few supplies only:
// no diamonds, no Pass XP and no chest marks, so the free-diamond rate and the
// season pass pace stay as they were. A finished one can still be claimed
// through the next hour, then it lapses.
//
// Every finished quest also fills the week's chest: 1 mark for a daily quest,
// 3 for a weekly one (30 a week at most). The chest has six steps along a
// visible track; no step pays diamonds (Cole, 2026-10-20: free diamonds stay rare; was 2 + 3 + 5) and one pays a Tournament Ticket. All diamonds from quests (the
// all-three daily bonus and the chest) are capped by QUEST_DIAMONDS_PER_WEEK,
// which together with the daily login reward stays below the free-diamond
// rate before the chest existed (tests/quests.test.ts). Nothing here is
// tradeable: coins, account-bound supplies, boost time, tickets and diamonds; no
// boss keys, legendary rolls or market items.
import { GROUNDS, REGIONS, type GroundDef } from './grounds.ts';
import { weekOf } from './recipes.ts';
import { SEALS } from './items.ts';
import { killCoins } from './xp.ts';
import { ratingOf } from './rating.ts';
import { gradeFloor, type Grade } from './grade.ts';
import { Rng } from './rng.ts';
import { CONFIG } from './config.ts';
import { multX } from './boosts.ts';
import { SPECIES, type RegionId } from './species.ts';
import { TICKET_SOURCES } from './tournament.ts';
import type { CreatureDTO } from './protocol.ts';

export type QuestKind = 'defeatRegion' | 'defeatLevel' | 'catch' | 'catchGrade' | 'catchBloodline' | 'catchSpecies' | 'bossWin' | 'arenaFight' | 'defeatHunt';

/**
 * One reward line. Boost amounts are minutes; a ticket is a Tournament Ticket (account-bound); auraCharm is free
 * Aura Charm time (shared/auracharm.ts), so catches under it are marked 'free'.
 */
export type RewardKind = 'coins' | 'diamonds' | 'item' | 'xpBoost' | 'coinBoost' | 'ticket' | 'auraCharm';
export interface Reward { k: RewardKind; n: number; item?: string }

export interface Quest {
  kind: QuestKind;
  need: number;
  progress: number;
  /** defeatRegion: the region. */
  region?: RegionId;
  /** defeatRegion: kills in a later region count too (weekly quests, so moving on mid-week never strands one). */
  orBeyond?: boolean;
  /** defeatLevel: the lowest hunt level that counts. */
  level?: number;
  /** catchGrade: the lowest grade that counts. */
  grade?: Grade;
  /** catchSpecies: the species to catch. */
  species?: string;
  /** defeatHunt: the hunt whose wilds count. Hourly catchSpecies: the hunt to go to for it. */
  ground?: string;
  /** What the quest pays. Quests made before 2026-10-14 have coins and seals instead (see questRewards). */
  rewards?: Reward[];
  coins?: number;
  seals?: { item: string; qty: number };
  claimed: boolean;
}

/**
 * One set of quests: a day's three, a week's three (then `day` is the week's Monday and `weekly` is set),
 * or an hour's one (then `day` is the UTC hour as YYYY-MM-DDTHH and `hourly` is set).
 */
export interface QuestDay {
  day: string; list: Quest[]; bonusClaimed: boolean; weekly?: boolean; hourly?: boolean;
  /** Weekly: chest marks earned this week, chest steps claimed, and the trainer level the chest's rewards are sized to. */
  marks?: number; chest?: number; chestLevel?: number;
}

/**
 * The trainer's quests: today's, and yesterday's while anything there is left
 * to claim; this week's, and last week's while anything there is left to claim;
 * this hour's (from Lv HOURLY_FROM), and the last hour's while it is left to claim.
 */
export interface QuestState { today: QuestDay; prev?: QuestDay; week?: QuestDay; prevWeek?: QuestDay; hour?: QuestDay; prevHour?: QuestDay }

/** Diamonds for finishing all three of a day's quests: none since 2026-10-14 (was 2) to keep free diamonds low. */
export const QUEST_BONUS_DIAMONDS = 0;
export const QUESTS_PER_DAY = 3;
/** Each quest pays this many kills' worth of coins at the trainer's best hunt. */
export const QUEST_COIN_KILLS = 100;
export const QUEST_SEALS = 10;
export const QUEST_POTIONS = 5;
/** Each weekly quest pays this many kills' worth of coins at the trainer's best hunt, and five times the daily supplies. */
export const QUEST_WEEKLY_COIN_KILLS = 500;
export const QUEST_WEEKLY_SEALS = 50;
export const QUEST_WEEKLY_POTIONS = 25;
/** XP boost minutes for a weekly boss or Arena quest. */
export const QUEST_CHALLENGE_BOOST_MIN = 30;

/** Hourly quests: from this trainer level, their targets, and their pay (kills' worth of coins at the best hunt, and supplies). */
export const HOURLY_FROM = 10;
export const HOURLY_DEFEAT = 150;
export const HOURLY_CATCH = 3;
export const HOURLY_COIN_KILLS = 30;
export const HOURLY_SEALS = 3;
export const HOURLY_POTIONS = 2;

/** Chest marks per finished quest. */
export const MARKS_DAILY = 1, MARKS_WEEKLY = 3;
/** The most marks a week can give: every daily and weekly quest. */
export const CHEST_MAX_MARKS = 7 * QUESTS_PER_DAY * MARKS_DAILY + QUESTS_PER_DAY * MARKS_WEEKLY;

/** The weekly chest's steps: marks needed and what each pays, sized to the trainer level the week began at. */
export const CHEST: { at: number; rewards: (trainerLevel: number) => Reward[] }[] = [
  { at: 3, rewards: () => [{ k: 'coinBoost', n: 30 }] },
  { at: 7, rewards: () => [{ k: 'xpBoost', n: 60 }] },
  { at: 12, rewards: (l) => [{ k: 'item', item: sealFor(bestHunt(l).level), n: 25 }] },
  { at: 17, rewards: () => [{ k: 'xpBoost', n: 60 }] },
  { at: 22, rewards: () => [{ k: 'coinBoost', n: 60 }, { k: 'ticket', n: TICKET_SOURCES.weeklyQuests }] },
  { at: 26, rewards: (l) => [{ k: 'xpBoost', n: 120 }, { k: 'item', item: potionFor(bestHunt(l).level), n: 10 }] },
];
export const CHEST_DIAMONDS = CHEST.reduce((a, s) => a + s.rewards(1).filter((r) => r.k === 'diamonds').reduce((b, r) => b + r.n, 0), 0);
/** The most diamonds quests pay in a week: seven all-three bonuses and the full chest. */
export const QUEST_DIAMONDS_PER_WEEK = 7 * QUEST_BONUS_DIAMONDS + CHEST_DIAMONDS;

/** Diamonds for finishing all three quests of a set: daily sets only; a week pays through its chest, an hour pays none. */
export const bonusOf = (d: QuestDay) => (d.weekly || d.hourly ? 0 : QUEST_BONUS_DIAMONDS);

/** Every set a trainer has open: yesterday, today, last week, this week, last hour, this hour. */
export const questSets = (s: QuestState | undefined): QuestDay[] => (s ? [s.prev, s.today, s.prevWeek, s.week, s.prevHour, s.hour].filter((d): d is QuestDay => !!d) : []);

const POOL: QuestKind[] = ['defeatRegion', 'defeatLevel', 'catch', 'catchGrade', 'catchBloodline', 'catchSpecies', 'bossWin', 'arenaFight'];
const BASIC: QuestKind[] = ['defeatRegion', 'defeatLevel', 'catch'];
const CATCHES: QuestKind[] = [...BASIC, 'catchGrade', 'catchBloodline', 'catchSpecies'];

export const utcDay = (ms: number) => new Date(ms).toISOString().slice(0, 10);
/** The UTC hour as YYYY-MM-DDTHH. */
export const utcHour = (ms: number) => new Date(ms).toISOString().slice(0, 13);

function daySeed(day: string): number {
  let h = 2166136261;
  for (let i = 0; i < day.length; i++) { h ^= day.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/**
 * Quest stages by trainer level: which kinds can be drawn for a day and for a
 * week, and how big the day's targets are (a week's are ten times as much;
 * seven times for grade catches; see WEEKLY_NEED for the boss and Arena). A
 * new trainer catches about 10-25 creatures and defeats about 1,100 wilds an
 * hour, and about 1 catch in 4 is Bloodline II or higher, 1 in 2 grade B or
 * better and 1 in 12 grade A or better. Boss quests start a little after
 * Fenrir opens (Lv 100); Arena quests when the Arena opens (Lv 150).
 */
export const QUEST_STAGES: { from: number; kinds: QuestKind[]; weekly: QuestKind[]; need: Record<QuestKind, number>; grade: Grade }[] = [
  { from: 1, kinds: BASIC, weekly: BASIC, need: { defeatRegion: 200, defeatLevel: 150, catch: 8, catchGrade: 0, catchBloodline: 0, catchSpecies: 0, bossWin: 0, arenaFight: 0, defeatHunt: 0 }, grade: 'B' },
  { from: 10, kinds: CATCHES, weekly: CATCHES, need: { defeatRegion: 500, defeatLevel: 300, catch: 12, catchGrade: 1, catchBloodline: 1, catchSpecies: 5, bossWin: 0, arenaFight: 0, defeatHunt: 0 }, grade: 'B' },
  { from: 30, kinds: CATCHES, weekly: CATCHES, need: { defeatRegion: 500, defeatLevel: 300, catch: 20, catchGrade: 1, catchBloodline: 2, catchSpecies: 8, bossWin: 0, arenaFight: 0, defeatHunt: 0 }, grade: 'A' },
  { from: 120, kinds: CATCHES, weekly: [...CATCHES, 'bossWin'], need: { defeatRegion: 500, defeatLevel: 300, catch: 20, catchGrade: 1, catchBloodline: 2, catchSpecies: 8, bossWin: 0, arenaFight: 0, defeatHunt: 0 }, grade: 'A' },
  { from: 150, kinds: CATCHES, weekly: [...CATCHES, 'bossWin', 'arenaFight'], need: { defeatRegion: 500, defeatLevel: 300, catch: 20, catchGrade: 1, catchBloodline: 2, catchSpecies: 8, bossWin: 0, arenaFight: 0, defeatHunt: 0 }, grade: 'A' },
];
/** Weekly targets set outright rather than as a multiple of a daily one. */
export const WEEKLY_NEED: Partial<Record<QuestKind, number>> = { bossWin: 1, arenaFight: 10 };

export function questStage(trainerLevel: number) {
  let st = QUEST_STAGES[0];
  for (const x of QUEST_STAGES) if (trainerLevel >= x.from) st = x;
  return st;
}

/** The set's three quest kinds: the same for every trainer at the same stage (the draw skips kinds the stage leaves out). */
export function questKinds(day: string, trainerLevel = Infinity, weekly = false): QuestKind[] {
  const rng = new Rng(daySeed(day));
  const pool = [...POOL];
  for (let i = pool.length - 1; i > 0; i--) { const j = rng.int(0, i); [pool[i], pool[j]] = [pool[j], pool[i]]; }
  const st = questStage(trainerLevel);
  const allowed = weekly ? st.weekly : st.kinds;
  return pool.filter((k) => allowed.includes(k)).slice(0, QUESTS_PER_DAY);
}

/** The highest hunt a trainer of this level can open. */
export function bestHunt(trainerLevel: number): GroundDef {
  let best = GROUNDS[0];
  for (const g of GROUNDS) if (g.level <= trainerLevel && g.level >= best.level) best = g;
  return best;
}

/** Seal tier a quest pays: the one a trainer at this hunt level would be buying. */
export function sealFor(huntLevel: number): string {
  return SEALS[huntLevel >= 150 ? 3 : huntLevel >= 60 ? 2 : huntLevel >= 20 ? 1 : 0];
}

/** Potion a quest pays: Healing Mead early, Nectar Flasks once hunts hit hard (Lv 60+). */
export function potionFor(huntLevel: number): string {
  return huntLevel >= 60 ? 'nectar' : 'mead';
}

/** A target: the stage's day target, or a week's. */
function needFor(kind: QuestKind, trainerLevel: number, weekly: boolean): number {
  if (weekly && WEEKLY_NEED[kind] !== undefined) return WEEKLY_NEED[kind]!;
  const day = questStage(trainerLevel).need[kind];
  return weekly ? day * (kind === 'catchGrade' ? 7 : 10) : day;
}

/** What a quest of this kind pays at this trainer level. */
export function rewardsFor(kind: QuestKind, trainerLevel: number, weekly: boolean): Reward[] {
  const h = bestHunt(trainerLevel).level;
  const coins: Reward = { k: 'coins', n: killCoins(h) * (weekly ? QUEST_WEEKLY_COIN_KILLS : QUEST_COIN_KILLS) };
  if (kind === 'defeatRegion' || kind === 'defeatLevel') return [coins, { k: 'item', item: potionFor(h), n: weekly ? QUEST_WEEKLY_POTIONS : QUEST_POTIONS }];
  if (kind === 'bossWin' || kind === 'arenaFight') return [coins, { k: 'xpBoost', n: QUEST_CHALLENGE_BOOST_MIN }];
  return [coins, { k: 'item', item: sealFor(h), n: weekly ? QUEST_WEEKLY_SEALS : QUEST_SEALS }];
}

/** A quest's rewards, reading the coins-and-seals form of quests made before rewards lists. */
export function questRewards(q: Quest): Reward[] {
  if (q.rewards) return q.rewards;
  const out: Reward[] = [];
  if (q.coins) out.push({ k: 'coins', n: q.coins });
  if (q.seals?.qty) out.push({ k: 'item', item: q.seals.item, n: q.seals.qty });
  return out;
}

/** The species for a species hunt: one of the upper hunts the trainer can open in their best region, drawn by the set's key. */
export function speciesFor(key: string, trainerLevel: number): string {
  const best = bestHunt(trainerLevel);
  const open = GROUNDS.filter((g) => g.region === best.region && g.level <= best.level && g.level >= best.level * 0.6);
  const pool = [...new Set((open.length ? open : [best]).map((g) => g.species))];
  return new Rng(daySeed(`species ${key}`)).pick(pool);
}

/** One fresh quest of a kind for a set keyed `key` (a day, or `week <Monday>`), sized to the trainer. */
export function makeQuest(key: string, kind: QuestKind, trainerLevel: number, weekly: boolean): Quest {
  const best = bestHunt(trainerLevel);
  // A hunt level a step or two below the best one open, so it is never out of reach.
  const lower = GROUNDS.filter((g) => g.region === best.region && g.level <= best.level * 0.75).reduce((a, g) => Math.max(a, g.level), 0) || GROUNDS[0].level;
  const q: Quest = { kind, need: needFor(kind, trainerLevel, weekly), progress: 0, rewards: rewardsFor(kind, trainerLevel, weekly), claimed: false };
  if (kind === 'defeatRegion') { q.region = best.region; if (weekly) q.orBeyond = true; }
  if (kind === 'defeatLevel') q.level = lower;
  if (kind === 'catchGrade') q.grade = questStage(trainerLevel).grade;
  if (kind === 'catchSpecies') q.species = speciesFor(key, trainerLevel);
  return q;
}

/**
 * The hunts an hourly quest can send a trainer to: the unlocked ones from half
 * the best hunt's level up to just below it, so it means moving but never
 * somewhere too hard. Falls back to any lower hunt, then the best one.
 */
export function hourlyHunts(trainerLevel: number): GroundDef[] {
  const best = bestHunt(trainerLevel);
  const lower = GROUNDS.filter((g) => g.level <= trainerLevel && g.id !== best.id && g.species !== best.species);
  const near = lower.filter((g) => g.level >= best.level * 0.5);
  return near.length ? near : lower.length ? lower : [best];
}

/** One hour's quest: defeat wilds on one of the trainer's lower hunts, or catch that hunt's species. */
export function makeHourlyQuest(hour: string, trainerLevel: number): Quest {
  const rng = new Rng(daySeed(`hour ${hour} ${trainerLevel}`));
  const g = rng.pick(hourlyHunts(trainerLevel));
  const h = bestHunt(trainerLevel).level;
  const coins: Reward = { k: 'coins', n: killCoins(h) * HOURLY_COIN_KILLS };
  if (rng.next() < 0.5) return { kind: 'defeatHunt', ground: g.id, species: g.species, need: HOURLY_DEFEAT, progress: 0, rewards: [coins, { k: 'item', item: potionFor(h), n: HOURLY_POTIONS }], claimed: false };
  return { kind: 'catchSpecies', ground: g.id, species: g.species, need: HOURLY_CATCH, progress: 0, rewards: [coins, { k: 'item', item: sealFor(h), n: HOURLY_SEALS }], claimed: false };
}

export function makeHourlyQuests(hour: string, trainerLevel: number): QuestDay {
  return { day: hour, list: [makeHourlyQuest(hour, trainerLevel)], bonusClaimed: false, hourly: true };
}

/**
 * Redraw one unfinished, unclaimed daily quest as a kind the day doesn't have yet (season pass, shared/pass.ts).
 * The new quest starts from zero with the rewards of its kind; returns false when nothing can be redrawn.
 */
export function rerollQuest(d: QuestDay, i: number, trainerLevel: number, pick: (n: number) => number): boolean {
  const q = d.list[i];
  if (!q || d.weekly || questDone(q) || q.claimed) return false;
  const kinds = questStage(trainerLevel).kinds.filter((k) => !d.list.some((x) => x.kind === k));
  if (!kinds.length) return false;
  d.list[i] = makeQuest(`${d.day} redraw ${i}`, kinds[Math.min(kinds.length - 1, Math.max(0, pick(kinds.length)))], trainerLevel, false);
  return true;
}

function makeSet(key: string, kinds: QuestKind[], trainerLevel: number, weekly: boolean): QuestDay {
  const list = kinds.map((kind) => makeQuest(key, kind, trainerLevel, weekly));
  return { day: key, list, bonusClaimed: false, ...(weekly ? { weekly: true, marks: 0, chest: 0, chestLevel: trainerLevel } : {}) };
}

export function makeQuests(day: string, trainerLevel: number): QuestDay {
  return makeSet(day, questKinds(day, trainerLevel), trainerLevel, false);
}

/** A week's three quests (the week's Monday as YYYY-MM-DD): the same kinds for every trainer at a stage, drawn apart from the daily ones. */
export function makeWeeklyQuests(week: string, trainerLevel: number): QuestDay {
  return makeSet(week, questKinds(`week ${week}`, trainerLevel, true), trainerLevel, true);
}

/**
 * The state for `day`: the same object if the day and its week are unchanged.
 * Otherwise a new day (or week) is made, keeping the previous day (or week)
 * while it has something to claim.
 */
export function rollQuests(s: QuestState | undefined, day: string, trainerLevel: number, hour?: string): QuestState {
  const week = weekOf(Date.parse(`${day}T00:00:00Z`));
  if (s?.week && s.week.marks === undefined) upgradeWeek(s, trainerLevel);
  const hourly = hour !== undefined && trainerLevel >= HOURLY_FROM;
  const hourSame = !hourly || s?.hour?.day === hour;
  if (s?.today.day === day && s.week?.day === week && hourSame) return s;
  let today = s?.today, prev = s?.prev;
  if (!today || today.day !== day) {
    prev = today && today.day < day && claimable(today).count && isYesterday(today.day, day) ? today : undefined;
    today = makeQuests(day, trainerLevel);
  }
  let wk = s?.week, prevWeek = s?.prevWeek;
  if (!wk || wk.day !== week) {
    prevWeek = wk && wk.day < week && claimable(wk).count && isLastWeek(wk.day, week) ? wk : undefined;
    wk = makeWeeklyQuests(week, trainerLevel);
  }
  let hr = s?.hour, prevHour = s?.prevHour;
  if (hourly && hr?.day !== hour) {
    prevHour = hr && claimable(hr).count && isLastHour(hr.day, hour!) ? hr : undefined;
    hr = makeHourlyQuests(hour!, trainerLevel);
  }
  return { today, ...(prev ? { prev } : {}), week: wk, ...(prevWeek ? { prevWeek } : {}), ...(hr ? { hour: hr } : {}), ...(prevHour ? { prevHour } : {}) };
}

function isLastHour(prev: string, hour: string) {
  return Date.parse(`${prev}:00:00Z`) + 3_600_000 === Date.parse(`${hour}:00:00Z`);
}

/**
 * A week made before the chest: its marks start from what is already done
 * this week (today's and yesterday's finished quests, and the weekly ones), and
 * its old all-three bonus, if already claimed, counts as the chest's first steps.
 */
function upgradeWeek(s: QuestState, trainerLevel: number) {
  const w = s.week!;
  const doneIn = (d?: QuestDay) => (d ? d.list.filter(questDone).length : 0);
  w.marks = Math.min(CHEST_MAX_MARKS, (doneIn(s.today) + doneIn(s.prev)) * MARKS_DAILY + doneIn(w) * MARKS_WEEKLY);
  w.chestLevel = trainerLevel;
  w.chest = w.bonusClaimed ? chestReached(w) : 0;
}

function isYesterday(prev: string, day: string) {
  return utcDay(Date.parse(`${day}T00:00:00Z`) - 86_400_000) === prev;
}

function isLastWeek(prev: string, week: string) {
  return utcDay(Date.parse(`${week}T00:00:00Z`) - 7 * 86_400_000) === prev;
}

export const questDone = (q: Quest) => q.progress >= q.need;

/** How many chest steps a week's marks have reached. */
export function chestReached(w: QuestDay): number {
  return CHEST.filter((s) => (w.marks ?? 0) >= s.at).length;
}

/** A pile of rewards added up. */
export interface Bundle { coins: number; diamonds: number; xpBoost: number; coinBoost: number; ticket: number; auraCharm: number; items: Record<string, number> }
export const emptyBundle = (): Bundle => ({ coins: 0, diamonds: 0, xpBoost: 0, coinBoost: 0, ticket: 0, auraCharm: 0, items: {} });
export function addReward(b: Bundle, r: Reward): Bundle {
  if (r.k === 'item') { if (r.item) b.items[r.item] = (b.items[r.item] ?? 0) + r.n; } else b[r.k] += r.n;
  return b;
}
export function addBundle(a: Bundle, b: Bundle): Bundle {
  a.coins += b.coins; a.diamonds += b.diamonds; a.xpBoost += b.xpBoost; a.coinBoost += b.coinBoost; a.ticket += b.ticket; a.auraCharm += b.auraCharm;
  for (const [k, v] of Object.entries(b.items)) a.items[k] = (a.items[k] ?? 0) + v;
  return a;
}

/** What a set still has to claim; count includes the all-three bonus and each chest step reached. */
export function claimable(d: QuestDay): Bundle & { count: number } {
  const out = { ...emptyBundle(), count: 0 };
  for (const q of d.list) {
    if (!questDone(q) || q.claimed) continue;
    out.count++;
    for (const r of questRewards(q)) addReward(out, r);
  }
  if (!d.weekly && bonusOf(d) > 0 && !d.bonusClaimed && d.list.every(questDone)) { out.diamonds += bonusOf(d); out.count++; }
  if (d.weekly) for (let i = d.chest ?? 0; i < chestReached(d); i++) { out.count++; for (const r of CHEST[i].rewards(d.chestLevel ?? 1)) addReward(out, r); }
  return out;
}

/** Mark everything claimable in a set as claimed. */
export function markClaimed(d: QuestDay) {
  for (const q of d.list) if (questDone(q)) q.claimed = true;
  if (!d.weekly && d.list.every(questDone)) d.bonusClaimed = true;
  if (d.weekly) d.chest = Math.max(d.chest ?? 0, chestReached(d));
}

/** Something that moves quests: a kill (with the hunt it was on), a catch, a boss or ladder win, or an Arena battle. */
export interface QuestEvent { kill?: GroundDef; caught?: CreatureDTO; bossWin?: boolean; arenaFight?: boolean }

/** A hunt event's progress on one quest. Returns true if it moved. */
export function questProgress(q: Quest, ev: QuestEvent): boolean {
  if (questDone(q)) return false;
  let n = 0;
  if (ev.kill) {
    if (q.kind === 'defeatRegion' && (ev.kill.region === q.region || (q.orBeyond && regionIndex(ev.kill.region) > regionIndex(q.region)))) n = 1;
    if (q.kind === 'defeatLevel' && ev.kill.level >= (q.level ?? 0)) n = 1;
    if (q.kind === 'defeatHunt' && ev.kill.id === q.ground) n = 1;
  }
  if (ev.caught) {
    const c = ev.caught;
    if (q.kind === 'catch') n = 1;
    if (q.kind === 'catchGrade' && ratingOf(c.rolls, c.species) >= gradeFloor(q.grade ?? 'A')) n = 1;
    if (q.kind === 'catchBloodline' && c.rolls.bloodline >= 1) n = 1;
    if (q.kind === 'catchSpecies' && c.species === q.species) n = 1;
  }
  if (ev.bossWin && q.kind === 'bossWin') n = 1;
  if (ev.arenaFight && q.kind === 'arenaFight') n = 1;
  if (!n) return false;
  q.progress = Math.min(q.need, q.progress + n);
  return true;
}

/**
 * Move today's and this week's quests with one event, and add chest marks to
 * this week for each quest it finishes. Returns true if anything moved.
 */
export function questEvent(s: QuestState, ev: QuestEvent): boolean {
  let moved = false;
  const step = (d: QuestDay | undefined, marks: number) => {
    if (!d) return;
    for (const q of d.list) {
      if (!questProgress(q, ev)) continue;
      moved = true;
      if (questDone(q) && s.week) s.week.marks = Math.min(CHEST_MAX_MARKS, (s.week.marks ?? 0) + marks);
    }
  };
  step(s.today, MARKS_DAILY);
  step(s.week, MARKS_WEEKLY);
  step(s.hour, 0);
  return moved;
}

const regionIndex = (r: RegionId | undefined) => REGIONS.findIndex((x) => x.id === r);

/** One line of plain copy for a quest. */
export function questText(q: Quest, regionName = (r: string) => r[0].toUpperCase() + r.slice(1)): string {
  const n = q.need.toLocaleString('en-US');
  switch (q.kind) {
    case 'defeatRegion': return `Defeat ${n} wilds in ${regionName(q.region ?? '')}${q.orBeyond && regionIndex(q.region) < REGIONS.length - 1 ? ' or beyond' : ''}`;
    case 'defeatLevel': return (q.level ?? 1) <= 1 ? `Defeat ${n} wilds on any hunt` : `Defeat ${n} wilds on a hunt of Lv ${q.level} or higher`;
    case 'catch': return `Catch ${n} creatures`;
    case 'catchGrade': return `Catch ${q.need === 1 ? 'a creature' : `${n} creatures`} of grade ${q.grade} or better`;
    case 'catchBloodline': return `Catch ${q.need === 1 ? 'a creature' : `${n} creatures`} of Bloodline II or higher`;
    case 'catchSpecies': return `Catch ${n} ${SPECIES[q.species ?? '']?.name ?? q.species}`;
    case 'bossWin': return q.need === 1 ? 'Win a boss fight' : `Win ${n} boss fights`;
    case 'arenaFight': return `Fight ${n} Arena battles`;
    case 'defeatHunt': { const g = GROUNDS.find((x) => x.id === q.ground); return `Defeat ${n} ${SPECIES[q.species ?? g?.species ?? '']?.name ?? q.species} on the Lv ${g?.level ?? '?'} hunt`; }
  }
}

/** Plain label for one reward, e.g. "10 Gold Seals" or "30 min Boost ×1.5 XP". */
export function rewardLabel(r: Reward, itemName = (id: string) => id): string {
  switch (r.k) {
    case 'coins': return `${r.n.toLocaleString('en-US')} coins`;
    case 'diamonds': return `${r.n} diamonds`;
    case 'xpBoost': return `${boostTime(r.n)} Boost ${multX(CONFIG.xpBoostMult)}`;
    case 'coinBoost': return `${boostTime(r.n)} Boost ${multX(CONFIG.coinBoostMult, 'coins')}`;
    case 'item': return `${r.n} ${itemName(r.item ?? '')}${r.n === 1 ? '' : 's'}`;
    case 'ticket': return `${r.n} Tournament Ticket${r.n === 1 ? '' : 's'}`;
    case 'auraCharm': return `${boostTime(r.n)} Aura Charm`;
  }
}
export const boostTime = (min: number) => (min % 60 === 0 ? `${min / 60} h` : `${min} min`);
